import { useState, useEffect } from 'react';
import { getCollection } from '../../services/firestoreService';
import { getAnalyticsEvents } from '../../services/analyticsService';
import { format } from 'date-fns';

interface SupportIssue {
  id: string;
  userId: string;
  email?: string;
  issueType: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  description: string;
  timestamp: any;
  status: 'open' | 'in-progress' | 'resolved';
  metadata?: Record<string, any>;
}

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  color: string;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, subtitle, color }) => (
  <div style={{
    backgroundColor: 'white',
    padding: '1.5rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderLeft: `4px solid ${color}`,
  }}>
    <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>{title}</p>
    <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>{value}</h2>
    {subtitle && <p style={{ margin: 0, color: '#999', fontSize: '0.75rem' }}>{subtitle}</p>}
  </div>
);

const SupportIssuesTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [supportIssues, setSupportIssues] = useState<SupportIssue[]>([]);
  const [purchaseErrors, setPurchaseErrors] = useState<any[]>([]);
  const [paymentFailures, setPaymentFailures] = useState<any[]>([]);
  const [subscriptionSyncIssues, setSubscriptionSyncIssues] = useState<any[]>([]);
  const [accountAccessIssues, setAccountAccessIssues] = useState<any[]>([]);
  const [trialIssues, setTrialIssues] = useState<any[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const issues: SupportIssue[] = [];

        // Get purchase errors
        try {
          const subscriptionEvents = await getAnalyticsEvents(30, undefined, undefined);
          const purchaseErrorsData = subscriptionEvents.filter(
            (e: any) => e.eventName === 'purchase_error' || 
                       e.eventName === 'purchase_failed' ||
                       e.properties?.error_category === 'purchase_error'
          );
          setPurchaseErrors(purchaseErrorsData);
          
          purchaseErrorsData.forEach((event: any) => {
            issues.push({
              id: event.id || `purchase_${event.userId}_${event.timestamp}`,
              userId: event.userId || 'unknown',
              email: event.properties?.email,
              issueType: 'Purchase Error',
              severity: 'high',
              description: event.properties?.error || event.properties?.error_message || 'Purchase failed',
              timestamp: event.timestamp,
              status: 'open',
              metadata: event.properties,
            });
          });
        } catch (e) {
          console.warn('Could not load purchase errors:', e);
        }

        // Get payment failures
        try {
          const paymentFailuresData = await getCollection(
            'analytics_subscription_events',
            [
              { field: 'eventType', operator: '==', value: 'payment_failed' }
            ],
            'timestamp',
            'desc',
            100
          );
          setPaymentFailures(paymentFailuresData);
          
          paymentFailuresData.forEach((event: any) => {
            issues.push({
              id: event.id || `payment_${event.userId}_${event.timestamp}`,
              userId: event.userId || 'unknown',
              email: event.email,
              issueType: 'Payment Failure',
              severity: 'high',
              description: `Payment failed for ${event.planId || 'subscription'}`,
              timestamp: event.timestamp,
              status: 'open',
              metadata: event,
            });
          });
        } catch (e) {
          console.warn('Could not load payment failures:', e);
        }

        // Get subscription sync issues
        try {
          const syncIssuesData = await getCollection(
            'analytics_subscription_events',
            [
              { field: 'eventType', operator: 'in', value: ['cross_platform_sync', 'sync_error'] }
            ],
            'timestamp',
            'desc',
            50
          );
          setSubscriptionSyncIssues(syncIssuesData);
          
          syncIssuesData.forEach((event: any) => {
            if (event.properties?.error || event.properties?.sync_error) {
              issues.push({
                id: event.id || `sync_${event.userId}_${event.timestamp}`,
                userId: event.userId || 'unknown',
                email: event.email,
                issueType: 'Subscription Sync Issue',
                severity: 'medium',
                description: event.properties?.error || event.properties?.sync_error || 'Cross-platform sync failed',
                timestamp: event.timestamp,
                status: 'open',
                metadata: event,
              });
            }
          });
        } catch (e) {
          console.warn('Could not load sync issues:', e);
        }

        // Get account access issues (login failures, account locks)
        try {
          const securityEvents = await getCollection(
            'security_events',
            [
              { field: 'event_type', operator: 'in', value: ['login_failed', 'account_locked'] }
            ],
            'timestamp',
            'desc',
            100
          );
          
          // Group by email and filter those with 5+ failures
          const emailFailures = new Map<string, any[]>();
          securityEvents.forEach((event: any) => {
            const email = event.email || 'unknown';
            if (!emailFailures.has(email)) {
              emailFailures.set(email, []);
            }
            emailFailures.get(email)!.push(event);
          });
          
          const accessIssues = Array.from(emailFailures.entries())
            .filter(([_, events]) => events.length >= 5)
            .map(([email, events]) => ({
              email,
              events,
              count: events.length,
            }));
          
          setAccountAccessIssues(accessIssues);
          
          accessIssues.forEach((issue) => {
            issues.push({
              id: `access_${issue.email}_${issue.events[0].timestamp}`,
              userId: issue.events[0].userId || 'unknown',
              email: issue.email,
              issueType: 'Account Access Issue',
              severity: 'high',
              description: `${issue.count} failed login attempts - account may be locked`,
              timestamp: issue.events[0].timestamp,
              status: 'open',
              metadata: { failureCount: issue.count, events: issue.events },
            });
          });
        } catch (e) {
          console.warn('Could not load account access issues:', e);
        }

        // Get trial issues
        try {
          const trialEvents = await getAnalyticsEvents(30, undefined, 'trial');
          const trialIssuesData = trialEvents.filter(
            (e: any) => e.eventName?.includes('trial_error') || 
                       e.properties?.trial_error ||
                       e.properties?.trial_issue
          );
          setTrialIssues(trialIssuesData);
          
          trialIssuesData.forEach((event: any) => {
            issues.push({
              id: event.id || `trial_${event.userId}_${event.timestamp}`,
              userId: event.userId || 'unknown',
              email: event.properties?.email,
              issueType: 'Trial Issue',
              severity: 'medium',
              description: event.properties?.trial_error || event.properties?.trial_issue || 'Trial access issue',
              timestamp: event.timestamp,
              status: 'open',
              metadata: event.properties,
            });
          });
        } catch (e) {
          console.warn('Could not load trial issues:', e);
        }

        // Sort by severity and timestamp
        issues.sort((a, b) => {
          const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
          const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
          if (severityDiff !== 0) return severityDiff;
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        });

        setSupportIssues(issues);
      } catch (error) {
        console.error('Error loading support issues:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading support issues...</p>
      </div>
    );
  }

  const criticalIssues = supportIssues.filter(i => i.severity === 'critical').length;
  const highIssues = supportIssues.filter(i => i.severity === 'high').length;
  const openIssues = supportIssues.filter(i => i.status === 'open').length;

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#ef4444';
      case 'high': return '#f59e0b';
      case 'medium': return '#6366f1';
      default: return '#6b7280';
    }
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Support User Issues
      </h1>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <KPICard
          title="Total Issues"
          value={supportIssues.length}
          subtitle="Last 30 days"
          color="#3b82f6"
        />
        <KPICard
          title="Critical Issues"
          value={criticalIssues}
          subtitle={criticalIssues > 0 ? '⚠️ Immediate attention' : 'None'}
          color={criticalIssues > 0 ? '#ef4444' : '#10b981'}
        />
        <KPICard
          title="High Priority"
          value={highIssues}
          subtitle="Requires review"
          color={highIssues > 0 ? '#f59e0b' : '#10b981'}
        />
        <KPICard
          title="Open Issues"
          value={openIssues}
          subtitle="Needs resolution"
          color={openIssues > 0 ? '#f59e0b' : '#10b981'}
        />
        <KPICard
          title="Purchase Errors"
          value={purchaseErrors.length}
          subtitle="Subscription purchase issues"
          color="#ef4444"
        />
        <KPICard
          title="Payment Failures"
          value={paymentFailures.length}
          subtitle="Payment processing issues"
          color="#f59e0b"
        />
        <KPICard
          title="Access Issues"
          value={accountAccessIssues.length}
          subtitle="Login/account access problems"
          color="#6366f1"
        />
        <KPICard
          title="Sync Issues"
          value={subscriptionSyncIssues.filter((e: any) => e.properties?.error).length}
          subtitle="Cross-platform sync problems"
          color="#8b5cf6"
        />
      </div>

      {/* Issues by Type */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
      }}>
        <h3 style={{ marginTop: 0 }}>Issues by Type</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <p style={{ margin: 0, color: '#666' }}>Purchase Errors</p>
            <h3 style={{ margin: '0.5rem 0' }}>{purchaseErrors.length}</h3>
          </div>
          <div>
            <p style={{ margin: 0, color: '#666' }}>Payment Failures</p>
            <h3 style={{ margin: '0.5rem 0' }}>{paymentFailures.length}</h3>
          </div>
          <div>
            <p style={{ margin: 0, color: '#666' }}>Account Access</p>
            <h3 style={{ margin: '0.5rem 0' }}>{accountAccessIssues.length}</h3>
          </div>
          <div>
            <p style={{ margin: 0, color: '#666' }}>Trial Issues</p>
            <h3 style={{ margin: '0.5rem 0' }}>{trialIssues.length}</h3>
          </div>
          <div>
            <p style={{ margin: 0, color: '#666' }}>Sync Issues</p>
            <h3 style={{ margin: '0.5rem 0' }}>{subscriptionSyncIssues.filter((e: any) => e.properties?.error).length}</h3>
          </div>
        </div>
      </div>

      {/* Critical Issues Alert */}
      {criticalIssues > 0 && (
        <div style={{
          backgroundColor: '#fef2f2',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #ef4444',
          marginBottom: '2rem',
        }}>
          <h3 style={{ marginTop: 0, color: '#ef4444' }}>🚨 Critical Issues Require Immediate Attention</h3>
          <p style={{ margin: '0.5rem 0' }}>
            {criticalIssues} critical issue(s) need immediate resolution.
          </p>
        </div>
      )}

      {/* Support Issues Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}>
        <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
          All Support Issues
        </h3>
        {supportIssues.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Time</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Type</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Severity</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>User Email</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Description</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {supportIssues.map((issue, index) => (
                <tr 
                  key={index} 
                  style={{ 
                    borderBottom: '1px solid #f0f0f0',
                    backgroundColor: issue.severity === 'critical' ? '#fef2f2' : 'transparent',
                  }}
                >
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                    {issue.timestamp?.toDate 
                      ? format(issue.timestamp.toDate(), 'MMM dd, yyyy HH:mm')
                      : format(new Date(issue.timestamp), 'MMM dd, yyyy HH:mm')}
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{issue.issueType}</td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      backgroundColor: getSeverityColor(issue.severity) + '20',
                      color: getSeverityColor(issue.severity),
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                    }}>
                      {issue.severity.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                    {issue.email || issue.userId.substring(0, 8) + '...'}
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{issue.description}</td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      backgroundColor: issue.status === 'open' ? '#fee2e2' : '#dcfce7',
                      color: issue.status === 'open' ? '#991b1b' : '#166534',
                      fontSize: '0.75rem',
                    }}>
                      {issue.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ padding: '1rem', color: '#666' }}>
            No support issues found in the last 30 days. All systems operational! 🎉
          </p>
        )}
      </div>
    </div>
  );
};

export default SupportIssuesTab;

